if (!customElements.get('finland-regional-map')) {
  class FinlandRegionalMap extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.defaultPanel = this.querySelector('[data-default-panel]');
      this.clearButton = this.querySelector('[data-region-clear]');
      this.regionSelect = this.querySelector('[data-region-select]');
      this.panelContainer = this.querySelector('[data-panel-container]');
      this.regionButtons = [...this.querySelectorAll('[data-region]')];
      this.regionPanels = new Map(
        [...this.querySelectorAll('[data-region-panel]')].map((panel) => [
          panel.dataset.regionPanel,
          panel,
        ]),
      );
      this.activeRegion = '';

      this.regionButtons.forEach((button) => {
        const slug = button.dataset.region;
        if (!this.regionPanels.has(slug)) {
          button.setAttribute('aria-disabled', 'true');
          button.setAttribute('tabindex', '-1');
        }
      });

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('keydown', this.handleKeydown.bind(this));
      this.regionSelect?.addEventListener('change', this.handleSelect.bind(this));
    }

    handleClick(event) {
      const clearButton = event.target.closest('[data-region-clear]');
      if (clearButton) {
        this.clearRegion();
        return;
      }

      const region = event.target.closest('[data-region]');
      if (!region || region.getAttribute('aria-disabled') === 'true') return;
      this.toggleRegion(region.dataset.region);
    }

    handleKeydown(event) {
      const region = event.target.closest('[data-region]');
      if (region && region.getAttribute('aria-disabled') !== 'true') {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.toggleRegion(region.dataset.region);
          return;
        }
      }

      if (event.key === 'Escape' && this.activeRegion) {
        event.preventDefault();
        const activeButton = this.regionButtons.find(
          (button) => button.dataset.region === this.activeRegion,
        );
        this.clearRegion();
        activeButton?.focus();
      }
    }

    handleSelect(event) {
      const slug = event.currentTarget.value;
      if (slug) {
        this.showRegion(slug);
      } else {
        this.clearRegion();
      }
    }

    toggleRegion(slug) {
      if (this.activeRegion === slug) {
        this.clearRegion();
      } else {
        this.showRegion(slug);
      }
    }

    stickyHeaderHeight() {
      if (!document.querySelector('store-header[data-is-sticky="true"]')) return 0;

      return (
        Number.parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
        ) || 0
      );
    }

    /**
     * Panels differ in height by thousands of pixels, so swapping one shortens
     * the document and the browser clamps the scroll position, stranding the
     * visitor at the page bottom. Pull the view back to the top of the panel,
     * and never scroll downwards so a visitor already above it stays put.
     */
    keepPanelInView() {
      if (!this.panelContainer) return;

      const panelTop = this.panelContainer.getBoundingClientRect().top + window.scrollY;
      const target = Math.max(0, panelTop - this.stickyHeaderHeight());

      if (window.scrollY > target) {
        window.scrollTo({ top: target, behavior: 'instant' });
      }
    }

    showRegion(slug) {
      const panel = this.regionPanels.get(slug);
      if (!panel) return;

      this.activeRegion = slug;
      this.defaultPanel.hidden = true;

      this.regionPanels.forEach((regionPanel, regionSlug) => {
        regionPanel.hidden = regionSlug !== slug;
      });

      this.regionButtons.forEach((button) => {
        const isActive = button.dataset.region === slug;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });

      if (this.regionSelect) this.regionSelect.value = slug;
      if (this.clearButton) this.clearButton.hidden = false;

      this.keepPanelInView();
    }

    clearRegion() {
      this.activeRegion = '';
      this.defaultPanel.hidden = false;
      this.regionPanels.forEach((panel) => {
        panel.hidden = true;
      });
      this.regionButtons.forEach((button) => {
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
      });
      if (this.regionSelect) this.regionSelect.value = '';
      if (this.clearButton) this.clearButton.hidden = true;

      this.keepPanelInView();
    }
  }

  customElements.define('finland-regional-map', FinlandRegionalMap);
}
